import type { BlobRow, OfflineDb } from './dexie/offline-db.js';

export interface NewBlobRecord {
  /** Local blob id — the value LocalPhotoSink returns as its "key" (task 4.4). */
  id: string;
  /** Future outbox item id (= clientRequestId, design D1). */
  outboxId: string;
  /** 'front' | 'back' | 'driver' | 'passenger' | 'incident' (design §5). */
  role: string;
  blob: Blob;
  contentType: string;
  bytes: number;
}

/**
 * DexieBlobStore — BlobStore port implementation over the Dexie `blobs`
 * table (design §5, task 4.3). Mirrors DexieKvStore's pattern: a concrete
 * class IS the port, no separate interface — callers depend on this class
 * directly (see LocalPhotoSink).
 *
 * Durability contract: `put()` does not resolve until Dexie's underlying
 * IndexedDB transaction has committed — a captured photo is durably on disk
 * before any caller can treat it as "captured" (critical requirement: zero
 * silent photo loss). A write failure (QuotaExceededError, iOS storage
 * eviction, Safari private-mode IndexedDB restrictions) rejects this
 * promise; it is NEVER caught/swallowed here — the caller (LocalPhotoSink,
 * then usePhotoUpload) is responsible for surfacing it to the operator.
 *
 * Cleanup: this class only exposes storage primitives (put/get/delete). The
 * "never delete a blob whose outbox item isn't synced/discarded" rule lives
 * one layer up, in blob-cleanup.ts — this class does not know about outbox
 * state at all, so it cannot accidentally violate that rule itself.
 */
export class DexieBlobStore {
  constructor(private readonly db: OfflineDb) {}

  async put(record: NewBlobRecord): Promise<void> {
    await this.db.blobs.put({
      id: record.id,
      outboxId: record.outboxId,
      role: record.role,
      blob: record.blob,
      contentType: record.contentType,
      bytes: record.bytes,
      // coordinator finding #5 — lets cleanupOrphanedBlobs age out a blob
      // whose form was abandoned before ever enqueueing (no outbox row is
      // EVER created for it in that case).
      createdAt: new Date().toISOString(),
    });
  }

  async get(id: string): Promise<BlobRow | undefined> {
    return this.db.blobs.get(id);
  }

  async getAllByOutboxId(outboxId: string): Promise<BlobRow[]> {
    return this.db.blobs.where('outboxId').equals(outboxId).toArray();
  }

  /**
   * getByIds (second adversarial review BLOCKER #1) — fetches EXACTLY the
   * given blob ids (`bulkGet`), silently skipping any id that no longer
   * exists. Used by `buildRealCheckinContext` so the handler loads exactly
   * `item.photoRefs`, never every blob merely grouped under an outboxId
   * (which can include a stale/orphaned row left over from a retake or a
   * pre-enqueue id change).
   */
  async getByIds(ids: string[]): Promise<BlobRow[]> {
    const rows = await this.db.blobs.bulkGet(ids);
    return rows.filter((row): row is BlobRow => row !== undefined);
  }

  async delete(id: string): Promise<void> {
    await this.db.blobs.delete(id);
  }

  /**
   * relinkToOutbox (second adversarial review BLOCKER #1) — updates the
   * `outboxId` FIELD (never the blob's own primary key/content) of every
   * given blob id to `newOutboxId`. Called at enqueue time so a form that
   * failed to enqueue once, got edited, and was minted a NEW
   * `clientRequestId` still has its already-captured blobs correctly
   * grouped under the CURRENT item — keeping `cleanupSyncedBlobs`'s
   * (Slice 4) own outboxId-based grouping logic correct. A missing id is a
   * safe no-op for that id (Dexie's `update()` resolves 0 either way).
   */
  async relinkToOutbox(ids: string[], newOutboxId: string): Promise<void> {
    await Promise.all(ids.map((id) => this.db.blobs.update(id, { outboxId: newOutboxId })));
  }
}
